// REMIX HMR BEGIN
if (!window.$RefreshReg$ || !window.$RefreshSig$ || !window.$RefreshRuntime$) {
  console.warn('remix:hmr: React Fast Refresh only works when the Remix compiler is running in development mode.');
} else {
  var prevRefreshReg = window.$RefreshReg$;
  var prevRefreshSig = window.$RefreshSig$;
  window.$RefreshReg$ = (type, id) => {
    window.$RefreshRuntime$.register(type, "\"app/routes/auth.login/route.tsx\"" + id);
  }
  window.$RefreshSig$ = window.$RefreshRuntime$.createSignatureFunctionForTransform;
}
var _s = $RefreshSig$();
import * as __hmr__ from "remix:hmr";
if (import.meta) {
  import.meta.hot = __hmr__.createHotContext(
  //@ts-expect-error
  "app/routes/auth.login/route.tsx");
  import.meta.hot.lastModified = "1784518634000";
}
// REMIX HMR END

/* eslint-disable jsx-a11y/label-has-associated-control */
import { Typography } from '@mui/material';
import { json, redirect } from '@remix-run/node';
import { useActionData } from '@remix-run/react';
import { isAxiosError } from 'axios';
import React from 'react';
import APIService from '~/services/ApiService';
import { isEmail } from '~/utils/Validator';
import { ErrorDisplay } from '../../components/ErrorDisplay';
import { commitSession, getSession } from '../../sessions';
import { authSession } from '~/constants/auth';
import { logoBlack } from '~/assets/images/logo';
import LoginForm from './LoginForm';
import VerifyOtpForm from './VerifyOtpForm';
import { LoginMessage } from './data';
export function ErrorBoundary({
  error
}) {
  return <div className="tw-bg-[#edede8] tw-w-full tw-pt-6">
      <ErrorDisplay message="Failed to login. Please try again." error={error} className="tw-max-w-screen-xl tw-m-auto" />
    </div>;
}
_c = ErrorBoundary;
export const loader = async ({
  request
}) => {
  const session = await getSession(request.headers.get('Cookie'));
  if (session.has('authToken')) {
    return redirect('/home');
  }
  const data = {
    error: session.get('error')
  };
  return json(data, {
    headers: {
      'Set-Cookie': await commitSession(session)
    }
  });
};
export const action = async ({
  request
}) => {
  const status = {
    type: undefined,
    message: ''
  };
  const apiService = new APIService(process.env.BASE_API_URL);
  const formData = await request.formData();
  const session = await getSession(request.headers.get('Cookie'));
  const email = String(formData.get('email'));
  const password = String(formData.get('password'));
  const rememberMe = Boolean(formData.get('rememberMe'));
  const actionType = String(formData.get('actionType') || 'login');
  const authenticatedSession = async accessToken => {
    if (!accessToken) {
      return json({
        status: {
          type: 'error',
          message: LoginMessage.UNAUTHORIZED
        }
      });
    }
    const authClaim = await apiService.AuthClaim(accessToken);
    session.set('authToken', accessToken);
    session.set('currentUser', authClaim.data['id']);
    session.set('isNeedToResetPassword', authClaim.data['isNeedToResetPassword']);
    session.set('rememberMe', rememberMe);
    session.set('roles', authClaim.data['roles']);
    const maxAge = rememberMe ? authSession['7day'] : authSession['24hrs'];
    return redirect('/home', {
      headers: {
        'Set-Cookie': await commitSession(session, {
          maxAge
        })
      }
    });
  };
  try {
    switch (actionType) {
      case 'login':
        {
          if (!email) {
            status.type = 'warning';
            status.message = LoginMessage.EMAIL_REQUIRED;
            return json({
              status
            });
          }
          if (!isEmail(email.toLowerCase())) {
            status.type = 'warning';
            status.message = LoginMessage.EMAIL_INVALID;
            return json({
              status
            });
          }
          const authTokenResponse = await apiService.GetToken(email, password);
          const mfaRequired = Boolean(authTokenResponse?.data?.['mfa_required']);
          const mfaToken = authTokenResponse?.data?.['mfa_token'];
          const maskedEmail = authTokenResponse?.data?.['email'];
          const maskedPhone = authTokenResponse?.data?.['phone'];
          const accessToken = authTokenResponse?.data?.['access_token'];
          if (!mfaRequired) {
            return authenticatedSession(accessToken);
          }
          return json({
            data: {
              mfaRequired,
              mfaToken,
              maskedEmail,
              maskedPhone
            }
          });
        }
      case 'verify_otp':
        {
          const mfaToken = String(formData.get('mfaToken'));
          const otp = String(formData.get('otp'));
          const verifyOtpResponse = await apiService.VerifyMfaOtp(mfaToken, otp);
          const accessToken = verifyOtpResponse.data['access_token'];
          if (!accessToken) {
            return json({
              status: {
                type: 'error',
                message: LoginMessage.VERIFY_OTP_INVALID
              }
            }, {
              status: 400
            });
          }
          return authenticatedSession(accessToken);
        }
      case 'resend_otp':
        {
          await apiService.ResendMfaOtp(String(formData.get('mfaToken')), String(formData.get('mfaChannel')));
          return json({
            status: {
              type: 'success',
              message: LoginMessage.REQUEST_OTP_SENT
            }
          });
        }
      case 'back_to_login':
        {
          return json({
            data: {
              mfaRequired: false
            }
          });
        }
      case 'default':
        {
          return json({
            status: {
              type: 'error',
              message: LoginMessage.UNKNOWN_ACTION
            }
          });
        }
    }
  } catch (error) {
    if (isAxiosError(error)) {
      session.flash('error', error.response?.data.message);
      status.type = 'error';
      status.message = error.response?.data.message;
      if (actionType === 'verify_otp') {
        if (status.message === 'Unauthorized') {
          status.type = 'error';
          status.message = LoginMessage.VERIFY_OTP_EXPIRED;
        } else if (status.message === 'Invalid OTP') {
          status.type = 'error';
          status.message = LoginMessage.VERIFY_OTP_INVALID;
        }
      } else if (actionType === 'resend_otp') {
        if (status.message === 'Unauthorized') {
          status.type = 'error';
          status.message = LoginMessage.RESEND_OTP_EXPIRED;
        }
      }
      return json({
        status
      });
    }
  }
  status.type = 'error';
  status.message = LoginMessage.UNHANDLED_ERROR;
  return json({
    status
  });
};
export const meta = () => {
  return [{
    title: 'Human Wealth Creation Index'
  }];
};
const Login = () => {
  _s();
  const actionData = useActionData();
  const message = actionData && 'status' in actionData && actionData.status?.message;
  const mfaRequired = actionData && 'data' in actionData && actionData.data?.mfaRequired;
  const screenAction = (() => {
    if (mfaRequired || message === LoginMessage.VERIFY_OTP_INVALID) {
      return 'verify_otp';
    }
    return 'login';
  })();
  return <>
      <div className="rounded-sm bg-white">
        <div className="flex flex-wrap items-center">
          <div className="left-side-login hidden w-full h-screen xl:w-2/3 xl:block bg-[url('/assets/images/login/login.jpg')] bg-cover bg-no-repeat">
            <div className="relative w-full h-full flex items-center">
              <div className="block pl-[10%]">
                <Typography variant="h3" className="text-doctor font-bold">
                  Welcome to Human Wealth Creation Index
                </Typography>
                {/* <h5 className="text-doctor font-bold text-[48px] mb-[24px]">
                 </h5> */}
              </div>
            </div>
          </div>

          <div className="right-side-login w-full h-screen relative flex flex-col items-center border-stroke xl:w-1/3 xl:border-l-2 justify-center bg-[url(/assets/images/login/crosslink-transparant.svg)] bg-cover bg-no-repeat">
            <div className="image m-0 top-[60px]">
              <img src={logoBlack} alt="logo" width="300px"></img>
            </div>
            <div className="container relative flex justify-center overflow-auto">
              {screenAction === 'login' && <LoginForm actionData={actionData} />}
              {screenAction === 'verify_otp' && <VerifyOtpForm actionData={actionData} />}
            </div>
          </div>
        </div>
      </div>
    </>;
};
_s(Login, "fHVw5pq0Zwd2gXh2gyrnVdHnLCc=", false, function () {
  return [useActionData];
});
_c2 = Login;
export default Login;
var _c, _c2;
$RefreshReg$(_c, "ErrorBoundary");
$RefreshReg$(_c2, "Login");

window.$RefreshReg$ = prevRefreshReg;
window.$RefreshSig$ = prevRefreshSig;